// src/apiClient.js
import { logDebug, logError, logInfo, logWarn } from './logging/loggingService';
import {
    clearStoredTokens,
    getStoredAccessToken,
    getStoredRefreshToken,
    getTokenExpiration,
    storeTokens,
} from './utils/tokenStorage';
import { API_BASE_URL } from './utils/apiBaseUrl';
import { getBearerToken } from './auth/tokenProvider';
import { isKeycloakMode } from './auth/keycloakClient';
import { getAuthEscalationSuppressionReason, isAuthEscalationSuppressed } from './auth/authEscalationGuard';
import { decodeJwtClaims } from './utils/tokenStorage';

const DEFAULT_TIMEOUT_MS = 20000; // 20s
const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000; // 5 minutes
const REFRESH_ENDPOINT = '/api/user/token/refresh';

let globalAuthErrorHandler = null;
export function setAuthErrorHandler(fn) {
    globalAuthErrorHandler = fn;
}

let refreshPromise = null;

export async function refreshAccessToken({ forceRefresh = false, bufferMs } = {}) {
    return ensureFreshAccessToken({}, { forceRefresh, bufferMs });
}

export async function apiFetch(path, options = {}) {
    const requestId = createRequestId();
    const stopTimer = startTimer();
    const { skipAuthRefresh, timeoutMs: providedTimeout, suppressAuthError, ...fetchOptions } = options;
    const method = (fetchOptions.method || 'GET').toUpperCase();

    const headers = new Headers(fetchOptions.headers || {});
    const isFormData = typeof FormData !== 'undefined' && fetchOptions.body instanceof FormData;

    if (!skipAuthRefresh && !isKeycloakMode()) {
        try {
            await ensureFreshAccessToken({
                method,
                path,
                requestId,
            });
        } catch (error) {
            logWarn('Pre-request token refresh failed', {
                component: 'apiClient',
                requestId,
                method,
                path,
            });
            throw error;
        }
    }

    const token = await getBearerToken();

    if (fetchOptions.body && !isFormData && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json; charset=utf-8');
    }
    if (!headers.has('Accept')) {
        headers.set('Accept', 'application/json');
    }

    if (token && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${token}`);
    }

    const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
    const controller = new AbortController();
    const timeoutMs = providedTimeout ?? DEFAULT_TIMEOUT_MS;
    const timeout = setTimeout(() => controller.abort(), timeoutMs);

    const requestMeta = {
        component: 'apiClient',
        requestId,
        method,
        path,
        url,
        timeoutMs,
        hasBody: Boolean(fetchOptions.body),
        isFormData,
        headers,
    };

    if (fetchOptions.body && !isFormData) {
        requestMeta.body = fetchOptions.body;
    }

    logDebug('Dispatching API request', requestMeta);

    let response;
    try {
        response = await fetch(url, {
            ...fetchOptions,
            headers,
            signal: controller.signal,
            credentials: fetchOptions.credentials ?? 'include',
        });
    } catch (error) {
        const durationMs = stopTimer();
        if (controller.signal.aborted || (error && error.name === 'AbortError')) {
            logWarn('API request aborted', {
                ...requestMeta,
                durationMs,
                error,
            });
        } else {
            logError('API request failed', error, {
                ...requestMeta,
                durationMs,
            });
        }
        clearTimeout(timeout);
        throw error;
    }

    clearTimeout(timeout);

    if (response.status === 401 || response.status === 403) {
        const durationMs = stopTimer();
        const authEscalationSuppressed = !suppressAuthError && isAuthEscalationSuppressed();
        logWarn('API authentication error', {
            ...requestMeta,
            status: response.status,
            durationMs,
            authEscalationSuppressed,
        });
        if (!suppressAuthError && !authEscalationSuppressed) {
            clearStoredTokens();
            if (typeof globalAuthErrorHandler === 'function') {
                globalAuthErrorHandler(response.status);
            }
        } else if (authEscalationSuppressed) {
            logInfo('Global auth escalation suppressed for API authentication error', {
                ...requestMeta,
                status: response.status,
                durationMs,
                suppressionReason: getAuthEscalationSuppressionReason(),
            });
        }
        const text = await response.text().catch(() => '');
        const payload = safeParse(text);
        if (isKeycloakMode()) {
            // Help debugging misconfiguration (issuer/jwks/time skew) without logging the full token.
            try {
                const wwwAuth = response.headers.get('WWW-Authenticate');
                const traceId = response.headers.get('X-Trace-Id');
                const claims = token ? decodeJwtClaims(token) : null;
                // eslint-disable-next-line no-console
                console.warn('[apiClient] Keycloak-mode 401/403', {
                    path,
                    status: response.status,
                    traceId,
                    wwwAuthenticate: wwwAuth,
                    backendError: typeof payload === 'object' ? payload?.error : undefined,
                    backendMessage: typeof payload === 'object' ? payload?.message : String(payload || ''),
                    tokenIss: claims?.iss,
                    tokenAud: claims?.aud,
                    tokenExp: claims?.exp,
                    tokenNbf: claims?.nbf,
                    nowEpochSeconds: Math.floor(Date.now() / 1000),
                });
            } catch {}
        }
        logDebug('API authentication payload', {
            ...requestMeta,
            status: response.status,
            durationMs,
            payload,
        });
        throw buildHttpError(response.status, payload);
    }

    if (!response.ok) {
        const durationMs = stopTimer();
        logWarn('API request returned non-OK status', {
            ...requestMeta,
            status: response.status,
            durationMs,
        });
        const text = await response.text().catch(() => '');
        const payload = safeParse(text);
        logDebug('API error payload', {
            ...requestMeta,
            status: response.status,
            durationMs,
            payload,
        });
        throw buildHttpError(response.status, payload);
    }

    const durationMs = stopTimer();
    logInfo('API request completed', {
        ...requestMeta,
        status: response.status,
        durationMs,
    });

    return response;
}

function safeParse(text) {
    try { return JSON.parse(text); } catch { return text; }
}

function buildHttpError(status, data) {
    const backendMessage = data && typeof data === 'object' && typeof data.message === 'string'
        ? data.message.trim()
        : '';
    const error = new Error(backendMessage);
    error.response = { status, data };
    return error;
}

function createRequestId() {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function startTimer() {
    const hasPerformance = typeof performance !== 'undefined' && typeof performance.now === 'function';
    const start = hasPerformance ? performance.now() : Date.now();
    return () => {
        const end = hasPerformance ? performance.now() : Date.now();
        return Math.round(end - start);
    };
}

async function ensureFreshAccessToken(meta = {}, options = {}) {
    const { forceRefresh = false, bufferMs } = options;
    const token = getStoredAccessToken();
    const refreshToken = getStoredRefreshToken();

    if (!token) {
        return;
    }

    if (!forceRefresh && !shouldRefreshToken(token, bufferMs)) {
        return;
    }

    if (!refreshToken) {
        clearStoredTokens();
        if (typeof globalAuthErrorHandler === 'function') {
            globalAuthErrorHandler(401);
        }
        throw createRefreshError(401, null);
    }

    if (!refreshPromise) {
        refreshPromise = performTokenRefresh(refreshToken, meta);
    }

    try {
        await refreshPromise;
    } catch (error) {
        clearStoredTokens();
        const status = error?.response?.status ?? 401;
        if (typeof globalAuthErrorHandler === 'function') {
            globalAuthErrorHandler(status);
        }
        throw error;
    } finally {
        refreshPromise = null;
    }
}

function shouldRefreshToken(token, bufferMs = TOKEN_REFRESH_BUFFER_MS) {
    const expiration = getTokenExpiration(token);
    if (!expiration) {
        return false;
    }
    const resolvedBuffer = typeof bufferMs === 'number' ? bufferMs : TOKEN_REFRESH_BUFFER_MS;
    const nowWithBuffer = Date.now() + resolvedBuffer;
    return expiration <= nowWithBuffer;
}

async function performTokenRefresh(refreshToken, meta) {
    const requestId = meta?.requestId ?? createRequestId();
    const stopTimer = startTimer();
    const url = `${API_BASE_URL}${REFRESH_ENDPOINT}`;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT_MS);

    const requestMeta = {
        component: 'apiClient',
        requestId,
        method: 'POST',
        path: REFRESH_ENDPOINT,
        url,
        isTokenRefresh: true,
    };

    logDebug('Refreshing access token', requestMeta);

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json; charset=utf-8',
                Accept: 'application/json',
            },
            body: JSON.stringify({ refreshToken }),
            credentials: 'include',
            signal: controller.signal,
        });

        const durationMs = stopTimer();

        if (!response.ok) {
            const text = await response.text().catch(() => '');
            const payload = safeParse(text);
            logWarn('Token refresh rejected', {
                ...requestMeta,
                status: response.status,
                durationMs,
                payload,
            });
            throw createRefreshError(response.status, payload);
        }

        const payload = await response.json().catch(() => null);

        if (!payload || typeof payload !== 'object' || !payload.accessToken) {
            logWarn('Token refresh response invalid', {
                ...requestMeta,
                status: response.status,
                durationMs,
                payload,
            });
            throw createRefreshError(response.status, payload);
        }

        storeTokens({ accessToken: payload.accessToken, refreshToken: payload.refreshToken });

        logInfo('Token refresh succeeded', {
            ...requestMeta,
            status: response.status,
            durationMs,
        });

        return payload.accessToken;
    } catch (error) {
        if (error?.response) {
            throw error;
        }

        if (error?.name === 'AbortError') {
            logWarn('Token refresh aborted', {
                ...requestMeta,
                error,
            });
        } else {
            logError('Token refresh failed', error, requestMeta);
        }

        throw createRefreshError(0, null, error);
    } finally {
        clearTimeout(timeout);
    }
}

function createRefreshError(status, payload, cause) {
    const error = new Error('Token refresh failed');
    error.response = { status, data: payload };
    if (cause) {
        error.cause = cause;
    }
    return error;
}
